// Entry point for the customer share-link viewer.
//
// Responsibilities, in order:
//   1. Parse `/preview/:code` (with optional `?fixture=1`) from the URL.
//   2. Render a loading state immediately so the page doesn't flash blank.
//   3. Fetch the share metadata from the backend (or fixture).
//   4. Branch on iOS Safari vs everything else.
//   5. Render header + (AR anchor | <model-viewer>) + footer.

import { fetchSharePayload } from "./api.ts";
import { isIosAr } from "./env.ts";
import { parseRoute } from "./router.ts";
import { renderHeader } from "./ui/header.ts";
import { renderFooter } from "./ui/footer.ts";
import { renderIosAr } from "./ui/ios-ar.ts";
import { renderFallbackViewer } from "./ui/fallback-viewer.ts";
import {
  renderError,
  renderExpired,
  renderLoading,
  renderNotFound,
} from "./ui/error-states.ts";

async function boot(): Promise<void> {
  const root = document.getElementById("root");
  if (!root) {
    // Defensive: index.html owns this id. If it's gone, something has
    // catastrophically diverged and there's no point continuing.
    return;
  }

  const route = parseRoute();
  if (!route) {
    swap(
      root,
      renderError(
        "Nothing to preview here",
        "Open a preview link from a contractor's estimate to see the deck.",
      ),
    );
    return;
  }

  swap(root, renderLoading());

  const result = await fetchSharePayload(route.code, route.fixture);

  if (result.kind === "expired") {
    swap(root, renderExpired());
    return;
  }
  if (result.kind === "not-found") {
    swap(root, renderNotFound());
    return;
  }
  if (result.kind === "error") {
    swap(
      root,
      renderError(
        "We couldn't load this preview",
        `${result.message}. Please try again in a moment.`,
      ),
    );
    return;
  }

  const payload = result.payload;
  const fragments: HTMLElement[] = [];
  fragments.push(renderHeader(payload));
  // AR Quick Look needs a USDZ; a GLB-only share uses <model-viewer> even
  // on iOS (it still orbits/zooms fine there).
  const usdzUrl = payload.usdzUrl;
  fragments.push(
    isIosAr() && usdzUrl
      ? renderIosAr({ ...payload, usdzUrl })
      : renderFallbackViewer(payload),
  );
  fragments.push(renderFooter(payload));
  swap(root, ...fragments);
}

function swap(root: HTMLElement, ...nodes: HTMLElement[]): void {
  root.replaceChildren(...nodes);
}

void boot();
